import { ArrowForward } from '@mui/icons-material';
import {
    Alert,
    Button,
    FormHelperText,
    Modal,
    Snackbar,
    TextField
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { Col, Row } from 'antd';
import Loading from 'components/Loading';
import { difference, filter, find, get } from "lodash";
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleCheckCode, handleCreateClass, handleGetClass, handleGetClassByCode, handleUpdateClass } from 'store/classes/thunks';
import { useCurrentOrgPermission } from 'store/organizations/selectors';
import { handleRemoveBooksFromClass } from "store/userBooks/thunks";
import { handleCreateUserClass, handleListUserClasses } from 'store/userClasses/thunks';
import { BirdLogoLarge, ClassLeftImg, ClassLeftNewImg, ClassLeftNewLogo } from "utils/assets/images";
import COLOR from 'utils/constants/color';
import { disableBodyScroll } from 'utils/methods/math';
import { validateData } from 'utils/methods/validateData';

import SelectableBookByGroup from 'components/SelectBookByGroup';
import styled from 'styled-components';
import { useBookOrder } from 'store/books/selectors';
import { handleUpdateAndDeleteSubMission } from 'store/activities/thunks';





//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------

// Styled-components for layout and basic elements
const Description = styled.span`
    color: #000000;
    display: block;
    font-size: 17px;
    font-weight: 300;
    line-height: 21px;
    margin-bottom: 25px;
    @media (max-width: 767px) {
        font-size: 15px;
        line-height: 21px;
    }
`;

const Header = styled(Description)`
    color: #35383d;
    font-size: 32px;
    font-weight: 500;
    line-height: 28px;
    margin-top: 26px;
    margin-bottom: 14px;
    @media (max-width: 767px) {
        margin-top: 4px;
        margin-bottom: 7px;
        font-size: 22px;
    }
`;

const Wrapper = styled.form`
    margin: 0px auto;
    width: 100%;
    overflow-y: auto;
    height: 100%;
    overflow-x: hidden;
    justify-content: center;
    align-items: center;
`;

const Text = styled.div`
    font-size: 15px;
    color: ${COLOR.black300};
    white-space: pre-line;
    text-align: left;
`;

const ClassNameError = styled.div`
    font-size: 12px;
    color: ${COLOR.radish100};
    white-space: pre-line;
    text-align: left;
`;

const LeftBannerImg = styled.img`
    width: 20% !important;
    height: 100%;
    position: fixed;
`;

const LeftBannerLogo = styled.div`
    position: fixed;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    left: 50px;
    @media (min-width: 1100px) and (max-width: 1420px) {
        width: 15%;
    }
    @media (min-width: 768px) and (max-width: 1100px) {
        width: 17%;
        left: 20px;
    }
`;

const BookSection = styled.div`
    width: 100%;
`;

const BookErrorSec = styled.div`
    padding-left: 16px;
`;

const Legend = styled.div`
    color: #383a3e;
    display: block;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    margin-left: 10px;
    margin-bottom: 16px;
`;

const ModalContent = styled.div`
    align-items: center;
    background-color: ${COLOR.white};
    border-radius: 3px;
    display: inline-flex;
    flex-direction: column;
    left: 50%;
    max-width: 320px;
    padding: 16px;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translate(-50%, -50%);
`;

const ModalRow = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-top: 16px;
    width: 100%;
`;

const BtnContainer = styled.div`
    display: flex;
    flex-direction: column;
`;


const StyledTextField = muiStyled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: theme.palette.grey[300],
        },
        '&:hover fieldset': {
            borderColor: theme?.palette?.primary?.main,
        },
    },
}));

const StyledModal = muiStyled(Modal)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledAlert = muiStyled(Alert)(({ theme }) => ({
    width: '100%',
}));

const ClassForm = ({
    data,
    setData,
    currentOrg,
    currentUser,
    onClosed,
    currentClass,
    userClasses
}) => {
    const orgPermissions = useCurrentOrgPermission()
    const isUpdate = data.id != undefined || data.editing;
    const isReviewAccount = currentUser?.reviewAccount || false
    const [selectedBook, setSelectedBook] = React.useState([]);
    const [booksError, setBooksError] = useState(false);
    const [noLicensesError, setNoLicensesError] = useState(false);
    const dispatch = useDispatch();
    const [classCodeError, setClassCodeError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [openReviewDialog, setReviewDialog] = React.useState(false);
    const [deleteBookModal, setDeleteBookModal] = React.useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [deletableBookName, setDeletableBookName] = React.useState('');
    const [dataRemovedBooks, setDataRemovedBooks] = React.useState([]);
    const [errorClassPresent, setErrorClassPresent] = useState(false);
    const [assignableGroup,setAssignableGroup] = useState([])
    const [originalBooksGroup, setOriginalBooksGroup] = useState([]);
    const bookOrder = useBookOrder();

    useEffect(() => {
        if (!data?.code) {
            generateCode();
        }
    }, [])

    const generateCode = async () => {
        const code = [...Array(10)].map(i=>(~~(Math.random()*36)).toString(36)).join('');
        const exists = await dispatch(handleCheckCode(code));

        if (exists) {
            generateCode();
        } else {
            setData({
                ...data,
                'code': code
            });
        }
    };
    
    const checkCode = async e => {
        if(isUpdate){
            if(data.code === e.target.value){
                setClassCodeError(false)
                return
            }
        }
        const exists = await dispatch(handleCheckCode(e.target.value));
        if (exists) {
            setClassCodeError(true)
        } else {
            setClassCodeError(false)
        }
    };

    const handleChange = e => {
        const { id, value } = e.target;
        let _value = value;
        if(id=="code"){
            _value = value.trim()
        }
        const maxLength = 60;
        const trimmedValue = _value.slice(0, maxLength);
        setData({
            ...data,
            books_group:selectedBook,
            [e.target.id]: trimmedValue
        });
    };

    useEffect(()=>{
       
    },[data])
    const checkDisplayName = async (value)=> {
        const _displayName =  value.trimEnd();
        let _userclasses=filter(userClasses,e=>(isUpdate?e.id==currentClass.id?false:true:true))
        let hasClassName =find(_userclasses,e=>e.displayName?.toLocaleLowerCase() === _displayName?.toLocaleLowerCase())
        handleChange({target:{id:"displayName",value:_displayName}})
        if(hasClassName){
            setErrorClassPresent(true)
            return false
           }else{
            setErrorClassPresent(false)
            return true
           }
    }
    const submit = async (e) => {
        e.preventDefault();
        if(isReviewAccount){
           setReviewDialog(true)
            return;
        }
        setBooksError(selectedBook.length === 0);
        if(selectedBook.length === 0){
            return;
        }
        if(checkDisplayName(data.displayName)===false){
            return;
        }
        if(isUpdate){
            const preSelectedBooks = originalBooksGroup.length > 0 ? originalBooksGroup : (data.books_group||[]);
            const removedBooks = difference(preSelectedBooks, selectedBook);
            setDataRemovedBooks(removedBooks);
            if (removedBooks.length>0) {    
                setDeleteBookModal(true)
            }
            else{
                infoUpdateClass();
            }
           
            return;
        }
        setLoading(true);
        let _code = data.code;
        const _codee=e.target.value?e.target.value:_code
        const exists = await dispatch(handleCheckCode(_codee));
        if(!exists){
            var booksUser = [];
            if(selectedBook.length > 0){
                setBooksError(false);
                booksUser = {
                    "books_group": selectedBook,
                    "userId": "",
                    "validityDate": "2022-01-01",
                    "orgId": currentOrg?.id
                }
                let _data={
        
                    orgId: currentOrg?.id,
                    teacherId: currentUser?.id,
                    ...data
                }
                let validStatus=validateData(booksUser,["books_group","orgId"])
                let validStatus2=validateData(_data,["code","displayName","orgId","teacherId"])
                if(!validStatus.isValid||!validStatus2.isValid){
                    console.log(validStatus.reason,validStatus2.reason,"invalid")
                    return;
                }
                const classObj = await dispatch(handleCreateClass({
                  ..._data 
                },{...booksUser}));

                await dispatch(handleCreateUserClass({ classId: classObj?.id, userId: currentUser?.id }));
                setData({});
                onClosed(true,classObj?.id);
                setSelectedBook([])
            }else{
                setBooksError(true);
            }
        }else{
            setClassCodeError(true);
        }
        setLoading(false);
    };
    const infoUpdateClass = async (e) => {
        if(dataLoading){
            return;
        }
        let codeFinal;
        if(!data) return;
        setDataLoading(true);
        setLoading(true);
        if(selectedBook.length > 0){
            if(data.code == undefined){
                codeFinal = currentClass.code;
            }else{
                codeFinal = data.code;
            }
            let displayNameFinal;
            if(data.displayName == undefined){
                displayNameFinal = currentClass.displayName;
            }else{
                displayNameFinal = data.displayName;
            }
            let codeClass;
            if(codeFinal != currentClass.code){
                codeClass = await dispatch(handleGetClassByCode(data.code.trim()));
            }         
            // await handleDeletedBook(dataRemovedBooks)
            data["books_group"]= selectedBook
            let booksUser = {
                "books_group": selectedBook,
                "orgId": currentOrg?.id
            }
            if(codeClass == undefined){
            const preSelectedBooks = originalBooksGroup.length > 0 ? originalBooksGroup : (data.books_group||[]);
            const removedBooks = difference(preSelectedBooks, selectedBook);
            const removedBooksOrderId = removedBooks?.map((keyName) => {
               const filterBookOrder =  bookOrder?.find((x) => x?.key === keyName)
                return filterBookOrder?.order;
            })?.flat();

            await dispatch(handleUpdateAndDeleteSubMission({bookIds: removedBooksOrderId, class_id: currentClass.id, user_id: currentUser?.id}));
           
            await dispatch(handleUpdateClass(currentClass.id, {
                    ...data,
                    displayName: displayNameFinal,
                    code: codeFinal
                },booksUser,data.userBookId));
                currentClass.displayName = displayNameFinal;
                currentClass.code = codeFinal;
                await dispatch(handleListUserClasses(currentUser?.id));
                await(dispatch(handleGetClass(currentClass.id)))
            }else{
            setBooksError(true);
            setLoading(false);
            }
        }
        setLoading(false);
        onClosed(false);
        setSelectedBook([])
        setDataLoading(false);
        setDeleteBookModal(false);
        setOriginalBooksGroup([]);
    }
    const handleDeletedBook = (group_to_remove) => {
        try {
            if (Array.isArray(group_to_remove) && group_to_remove.length > 0) {
                dispatch(handleRemoveBooksFromClass({
                    classId: currentClass.id,
                    group_to_remove
                }));
            }
        } catch (error) {
            Sentry.captureException(`586 Error in handleDeletedBook: ${error}`);
            console.log(error, "Error in deleting book related data")
        }
    }
    useEffect(() => {
        const bookPermissions = get(orgPermissions, "books_group_permission", [])
        // const user_books_group = userBook?.books_group = [];
        if(bookPermissions&&bookPermissions.length>0){
              setAssignableGroup(bookPermissions)
            // if(user_books_group&&user_books_group.length>0){
            //     setAssignableGroup(bookPermissions)
            // }else{
              
            // }
        }

    }, [orgPermissions])
        useEffect(()=>{
            if(Array.isArray(data.books_group)&&data.books_group.length>0){
                setSelectedBook(data.books_group)
                if(isUpdate && originalBooksGroup.length === 0 && data.id){
                    setOriginalBooksGroup([...data.books_group])
                }
            }
        },[data]) 


    useEffect(() => {
        document.documentElement.style.overflow = "hidden";
        disableBodyScroll('class');
      
        return () => {
          document.documentElement.style.overflow = "auto";
          document.body.style.overflow = "auto";
        };
      }, []);
      
    return (
        <Wrapper id="classCreationForm" onSubmit={submit}>
            <StyledModal
                open={deleteBookModal}
                onClose={() => setDeleteBookModal(false)}
            >
                <ModalContent>
                    <Text><strong>{`Deleting ${deletableBookName} book from your class will also delete the following:`}</strong></Text>
                    <Text>{"—All grades and assessments submitted\n—All data related to this book (notes, etc.)\n—You will never be able to get this data again Are you sure you want to delete it? "}</Text>
                    <ModalRow>
                        <Button
                            style={{ marginRight: '4px' }}
                            onClick={() => setDeleteBookModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                                document.body.classList.add('scroll-enabled');
                                infoUpdateClass();
                            }}
                            disabled={dataLoading}
                        >
                            {dataLoading ? <Loading size={20} white /> : 'Yes'}
                        </Button>
                    </ModalRow>
                </ModalContent>
            </StyledModal>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={openReviewDialog}
                autoHideDuration={3000}
                onClose={() => setReviewDialog(false)}
            >
                <StyledAlert onClose={() => setReviewDialog(false)} severity="error">
                    <strong>ALERT:</strong> Only for Review!
                </StyledAlert>
            </Snackbar>

            <Row gutter={[25, 0]}>
                <Col xs={24} sm={24} md={6} xl={6} className="addInfoLogoUpr">
                    <div className="addInfoLogo addInfoLogoWeb">
                        <LeftBannerImg src={ClassLeftImg} />
                        <LeftBannerImg src={ClassLeftNewImg} />
                        <LeftBannerLogo>
                            <img src={ClassLeftNewLogo} alt="Class Logo" />
                        </LeftBannerLogo>
                    </div>
                    <div className="addInfoLogoMob">
                        <img src={BirdLogoLarge} alt="Bird Logo" />
                    </div>
                </Col>
                <Col xs={24} sm={24} md={18} xl={16} className="spcRght">
                    <Header>{isUpdate ? "Update a class" : "Create a Class"}</Header>
                    <Description>You can create your class periods in this section. Remember to name your classes after the subject's names and levels to make it easier on you and your students using NosotrosOnline.</Description>
                    <Row gutter={[16, 24]} className="setLabel">
                        <Col span={16}>
                            <StyledTextField
                                fullWidth
                                id="displayName"
                                label="Class Name"
                                onChange={handleChange}
                                error={errorClassPresent}
                                onBlur={e => checkDisplayName(e.target.value)}
                                required
                                variant="outlined"
                                value={data?.displayName}
                                helperText={errorClassPresent && 'Please use different class name'}
                            />
                        </Col>
                        <Col span={8}>
                            <StyledTextField
                                fullWidth
                                id="code"
                                error={classCodeError}
                                label="Class Code"
                                onBlur={checkCode}
                                onChange={handleChange}
                                variant="outlined"
                                helperText={classCodeError && 'Class code not available'}
                                value={data?.code}
                            />
                        </Col>
                    </Row>

                    <p className="selectBooks">Select books for class</p>
                    <Row gutter={[16, 24]} className="setLabel">
                        <Col xs={24} md={12}>
                            <div>
                                <BookSection>
                                    <SelectableBookByGroup
                                        books_group={selectedBook}
                                        org_book_permissions={assignableGroup}
                                        from="class"
                                        onSelect={(item) => {
                                            setSelectedBook(item)
                                            if (item.length > 0) {
                                                setBooksError(false);
                                                setNoLicensesError(false);
                                            } else {
                                                setBooksError(true);
                                            }
                                        }}
                                    />
                                </BookSection>
                                <BookErrorSec>
                                    {noLicensesError &&
                                        <FormHelperText error>The selected school does not have enough licenses available</FormHelperText>
                                    }
                                </BookErrorSec>
                            </div>
                        </Col>
                    </Row>
                    <div>
                        <BtnContainer className="makeClassBtn mnageCreateBtn">
                            {booksError && <FormHelperText error>Choose at least one book</FormHelperText>}
                            <Button
                                disabled={!(data.displayName && data.code && !classCodeError) || loading || errorClassPresent}
                                variant="contained"
                                color="primary"
                                type='submit'
                                endIcon={<ArrowForward />}
                            >
                                {loading ? <Loading size={20} white /> : isUpdate ? "Update Class" : 'Create Class'}
                            </Button>
                        </BtnContainer>
                    </div>
                </Col>
            </Row>
        </Wrapper>
    );
};



export default ClassForm;